<template>
  <div class="kiosk-wrapper">
    <div class="kiosk-bg"></div>
    <div class="kiosk-overlay"></div>

    <div class="kiosk-clock">
      <div class="clock-time">{{ currentTime }}</div>
      <div class="clock-date">{{ currentDate }}</div>
    </div>

    <div class="kiosk-brand">
      <span class="brand-text">Conference Room Access</span>
    </div>

    <div class="kiosk-center">
      <div class="kiosk-card">
        <div v-if="!isUnlocked">
          <div class="card-header">
            <div class="lock-badge badge-blue">
              <svg viewBox="0 0 24 24" class="badge-icon">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h1 class="card-title">Computer Locked</h1>
            <p class="card-subtitle">
              Enter the 5-digit PIN sent to your email or SMS to access the
              conference room computer
            </p>
          </div>

          <div class="card-body">
            <div class="pin-dots">
              <div
                v-for="i in 5"
                :key="i"
                class="pin-digit"
                :class="{ filled: pin.length >= i, error: !!errorMessage }"
              >
                {{ pin.charAt(i - 1) }}
              </div>
            </div>
            <div :class="errorMessage ? 'pin-error' : 'pin-error-placeholder'">
              {{ errorMessage }}
            </div>

            <div class="numpad">
              <button
                v-for="num in [1, 2, 3, 4, 5, 6, 7, 8, 9]"
                :key="num"
                type="button"
                class="key-btn"
                @click="pressKey(num.toString())"
                :disabled="loading"
              >
                {{ num }}
              </button>
              <button
                type="button"
                class="key-btn key-del"
                @click="backspace"
                :disabled="loading || !pin.length"
              >
                ⌫
              </button>
              <button
                type="button"
                class="key-btn"
                @click="pressKey('0')"
                :disabled="loading"
              >
                0
              </button>
              <button
                type="button"
                class="key-btn key-submit"
                @click="submitPin"
                :disabled="pin.length !== 5 || loading"
              >
                <span v-if="!loading">🔓 Unlock</span>
                <span v-else class="submit-spinner"></span>
              </button>
            </div>
          </div>
          <div class="card-footer">
            ℹ️ Need help? Contact IT Department at 718-963-9260 #12340
            <div
              @click="administrativeAccessDialog = true"
              style="text-align: center; font-size: 13px; font-weight: 600; color: red; cursor: pointer; margin-top: 10px;"
            >
              Send a pin to Mr. Mandel
            </div>
          </div>
        </div>

        <div v-else>
          <div class="card-header success-header">
            <div class="lock-badge badge-green">
              <svg viewBox="0 0 24 24" class="badge-icon icon-check">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h1 class="card-title">Welcome</h1>
            <h2 class="success-name" v-if="bookingData.participants">
              {{
                bookingData.conferenceRoomUserID === null
                  ? bookingData.participants.find(
                      (p) => p.username === bookingData.username
                    ).adminPortalUser.name || bookingData.username
                  : bookingData.participants.find(
                      (p) =>
                        p.conferenceRoomUserID ===
                        bookingData.conferenceRoomUserID
                    ).conferenceRoomUser.fullName
              }}
            </h2>
          </div>
          <div class="card-body text-center">
            <p class="success-text">
              The computer has been unlocked successfully.
            </p>
            <div class="booking-chip">
              <span>🕐</span
              ><span>
                <strong
                  style="font-size: 18px !important;"
                  v-if="bookingData && bookingData.endTime"
                  >Booking valid until:
                  {{
                    new Date(bookingData.endTime).toLocaleTimeString()
                  }}</strong
                ></span
              >
            </div>
            <button
              type="button"
              :class="['start-btn', { filling: autoFilling }]"
              :style="{ '--fill-duration': autoStartDuration + 's' }"
              @click="startComputerUse"
            >
              Start Using Computer 💻
            </button>
          </div>
        </div>
      </div>
    </div>

    <v-dialog
      v-model="administrativeAccessDialog"
      width="440"
      content-class="admin-dialog"
    >
      <v-card class="admin-card">
        <div class="admin-card-header">
          <h2 class="admin-title">Administrative Access</h2>
        </div>
        <p class="admin-subtitle">Select the duration of hours.</p>
        <v-card-text class="admin-card-body">
          <v-select
            v-model="administrativeAccessDuration"
            :items="[
              { text: '30 minutes', value: 0.5 },
              { text: '1 hour', value: 1 },
              { text: '1.5 hours', value: 1.5 },
              { text: '2 hours', value: 2 },
              { text: '3 hours', value: 3 },
              { text: '4 hours', value: 4 },
              { text: '5 hours', value: 5 },
              { text: '6 hours', value: 6 },
              { text: '7 hours', value: 7 },
              { text: '8 hours', value: 8 },
              { text: '9 hours', value: 9 },
              { text: '10 hours', value: 10 },
            ]"
            item-text="text"
            item-value="value"
            label="Duration"
            dark
            solo
            flat
            class="admin-select"
          ></v-select>
          <p>By clicking confirm, the system will send a pin to Mr. Mandel.</p>
        </v-card-text>
        <v-card-actions class="admin-card-actions">
          <v-spacer></v-spacer>
          <v-btn color="white" flat @click="administrativeAccessDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            :loading="processing"
            class="admin-confirm-btn"
            @click="createAdministrativeAccess"
            >Confirm</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="waitForCodeDialog"
      width="380"
      content-class="admin-dialog"
    >
      <v-card class="admin-card">
        <v-card-text
          class="admin-card-body"
          style="text-align: center; padding-top: 28px !important;"
        >
          <p style="margin: 0;">
            Please wait to receive the code via SMS and/or email.
          </p>
        </v-card-text>
        <v-card-actions class="admin-card-actions">
          <v-spacer></v-spacer>
          <v-btn class="admin-confirm-btn" @click="waitForCodeDialog = false"
            >OK</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
export default {
  metaInfo: () => ({ title: "Conference Room Kiosk" }),
  name: "ConferenceRoomKiosk",
  data: () => ({
    pin: "",
    loading: false,
    isUnlocked: false,
    errorMessage: "",
    bookingData: null,
    currentTime: "",
    currentDate: "",
    clockTimer: null,
    autoFilling: false,
    autoStartTimer: null,
    autoStartDuration: 10,
    administrativeAccessDialog: false,
    waitForCodeDialog: false,
    administrativeAccessDuration: 1,
    processing: false,
  }),
  watch: {
    isUnlocked(val) {
      if (!val) return;
      this.autoFilling = true;
      this.autoStartTimer = setTimeout(
        () => this.startComputerUse(),
        this.autoStartDuration * 1000
      );
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handlePhysicalKeyboard);
    this.updateClock();
    this.clockTimer = setInterval(this.updateClock, 1000);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handlePhysicalKeyboard);
    clearInterval(this.clockTimer);
    clearTimeout(this.autoStartTimer);
  },
  methods: {
    updateClock() {
      const now = new Date();
      this.currentTime = now.toLocaleTimeString().split(" ")[0];
      this.currentDate = now.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    pressKey(char) {
      if (this.pin.length < 5) {
        this.pin += char;
        this.errorMessage = "";
      }
    },
    backspace() {
      this.pin = this.pin.slice(0, -1);
      this.errorMessage = "";
    },
    handlePhysicalKeyboard(e) {
      if (this.isUnlocked || this.loading) return;
      if (/^[0-9]$/.test(e.key)) this.pressKey(e.key);
      else if (e.key === "Backspace") this.backspace();
      else if (e.key === "Enter" && this.pin.length === 5) this.submitPin();
    },
    // createTestUser() {
    //   return {
    //     conferenceRoomUserID: null,
    //     username: "test@utaw.com",
    //     endTime: new Date(new Date().getTime() + 3.6e6).toISOString(), // 1 hour
    //     participants: [
    //       {
    //         username: "test@utaw.com",
    //         adminPortalUser: {
    //           name: "Test User",
    //         },
    //       },
    //     ],
    //   };
    // },
    async submitPin() {
      if (this.pin.length !== 5) return;
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await this.$http.post(
          `api/ConferenceRoomBookings/LoginToComputer?pin=${this.pin}`
        );

        this.bookingData = response.booking;
        this.isUnlocked = true;
        this.notify("success", "PIN verified successfully! Computer unlocked.");
      } catch (err) {
        // if (this.pin === "25839") {
        //   this.bookingData = this.createTestUser();
        //   this.isUnlocked = true;
        //   this.notify(
        //     "success",
        //     "PIN verified successfully! Computer unlocked."
        //   );
        //   return;
        // }
        this.handleError("Invalid or expired PIN code");
      }
      this.loading = false;
    },

    startComputerUse() {
      const socket = new WebSocket("ws://localhost:7197");

      socket.onopen = () => {
        socket.send(JSON.stringify({ action: "GET_SYSTEM_INFO" }));
        socket.send(JSON.stringify({ action: "UNLOCK_PC", pin: this.pin }));
      };

      socket.onmessage = (event) => {
        try {
          const response = JSON.parse(event.data);
          console.log("Received from server:", response);
        } catch (error) {
          console.error("Failed to parse server response:", error);
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket Error:", error);
      };

      socket.onclose = () => {
        console.log("WebSocket connection closed");
      };
    },

    handleError(msg) {
      this.errorMessage = msg;
      this.pin = "";
      this.notify("error", msg);
    },
    notify(type, text, title = null, duration = 30000) {
      this.$notify({ group: "notification", type, title, text, duration });
    },
    formatTime(iso) {
      return iso && window.moment ? window.moment(iso).format("HH:mm") : iso;
    },

    async createAdministrativeAccess() {
      this.processing = true;
      try {
        await this.$http.post(
          "api/ConferenceRoomBookings/createAdministrativeAccess",
          {
            description: "Administrative Access",
            startTime: moment().format("YYYY-MM-DD HH:mm"),
            endTime: moment()
              .add(this.administrativeAccessDuration, "hours")
              .format("YYYY-MM-DD HH:mm"),
            notification: {
              sendEmail: true,
              sendSMS: true,
            },
          }
        );
        this.waitForCodeDialog = true;
        this.administrativeAccessDialog = false;
      } catch (error) {
        alert("Failed to send administrative access.");
      }
      this.processing = false;
    },
  },
};
</script>

<style scoped>
.kiosk-wrapper,
.kiosk-wrapper * {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  box-sizing: border-box;
}
.kiosk-wrapper {
  position: fixed !important;
  inset: 0;
  z-index: 99999;
  overflow: hidden;
  background: #0f172a !important;
  color: #fff;
  user-select: none;
  -webkit-font-smoothing: antialiased;
}

.kiosk-bg {
  position: fixed;
  inset: 0;
  background: url("./conference-room.jpg") center/cover;
  filter: blur(3px) brightness(0.95);
  transform: scale(1.06);
  z-index: 0;
  pointer-events: none;
}
.kiosk-overlay {
  position: fixed;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(15, 23, 42, 0.88),
    rgba(30, 41, 59, 0.72),
    rgba(15, 23, 42, 0.92)
  );
  z-index: 1;
  pointer-events: none;
}
.kiosk-center {
  position: fixed;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
}

.kiosk-clock,
.kiosk-brand {
  position: fixed;
  top: 28px;
  z-index: 4;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}
.kiosk-clock {
  right: 36px;
  text-align: right;
}
.clock-time {
  font-size: 52px !important;
  font-weight: 200 !important;
  line-height: 1 !important;
  letter-spacing: 3px;
}
.clock-date {
  font-size: 13px !important;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 6px;
  margin-right: 15px;
}
.kiosk-brand {
  left: 36px;
  top: 38px;
}
.brand-text {
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 1.5px;
  text-transform: uppercase;
}

.kiosk-card {
  width: 100%;
  max-width: 550px;
  background: rgba(15, 22, 40, 0.82);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.6);
}
.card-header {
  padding: 36px 32px 22px;
  text-align: center;
  background: linear-gradient(180deg, rgba(96, 165, 250, 0.07), transparent);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.success-header {
  background: linear-gradient(180deg, rgba(34, 197, 94, 0.12), transparent);
}

.lock-badge {
  width: 74px;
  height: 74px;
  margin: 0 auto 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.badge-blue {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  box-shadow: 0 10px 32px rgba(59, 130, 246, 0.45);
}
.badge-green {
  width: 84px;
  height: 84px;
  background: linear-gradient(135deg, #22c55e, #15803d);
  box-shadow: 0 10px 32px rgba(34, 197, 94, 0.45);
}
.badge-icon {
  width: 36px;
  height: 36px;
  fill: none;
  stroke: #fff;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.icon-check {
  width: 44px;
  height: 44px;
}

.card-title {
  font-size: 26px !important;
  font-weight: 600 !important;
  margin: 0 0 10px !important;
  color: #fff;
}
.card-subtitle {
  font-size: 13px !important;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 !important;
  line-height: 1.65 !important;
}
.success-name {
  font-size: 21px !important;
  color: #86efac;
  margin: 4px 0 0 !important;
  font-weight: 400 !important;
}
.success-text {
  color: rgba(255, 255, 255, 0.904) !important;
  font-size: 16px;
  margin-bottom: 16px;
}
.text-center {
  text-align: center;
}

/* PIN */
.card-body {
  padding: 26px 30px 20px;
}
.pin-dots {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-bottom: 10px;
}
.pin-digit {
  width: 48px;
  height: 56px;
  border-radius: 10px;
  border: 2px solid rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 500;
  color: #fff;
}
.pin-digit.filled {
  background: rgba(96, 165, 250, 0.15);
  border-color: #60a5fa;
  box-shadow: 0 0 14px rgba(96, 165, 250, 0.35);
}
.pin-digit.error {
  background: rgba(239, 68, 68, 0.15);
  border-color: #ef4444;
  box-shadow: 0 0 14px rgba(239, 68, 68, 0.35);
  color: #fca5a5;
}
.pin-error,
.pin-error-placeholder {
  min-height: 26px;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}
.pin-error {
  color: #f87171;
}

.numpad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 12px;
}
.key-btn {
  height: 66px;
  font-size: 22px !important;
  font-weight: 500 !important;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: background 0.14s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: none !important;
  margin: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
  outline: none !important;
}
.key-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.13);
  transform: translateY(-1px);
}
.key-btn:active:not(:disabled) {
  background: rgba(255, 255, 255, 0.18);
  transform: scale(0.95);
}
.key-btn:disabled {
  opacity: 0.35;
  cursor: default;
}
.key-del {
  color: #fca5a5;
  font-size: 18px !important;
}
.key-submit {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border: none;
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.4);
  font-size: 15px !important;
}
.key-submit:hover:not(:disabled) {
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.58);
}
.submit-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: kiosk-spin 0.7s linear infinite;
}

@keyframes kiosk-spin {
  to {
    transform: rotate(360deg);
  }
}

.card-footer {
  padding: 13px 30px 18px;
  text-align: center;
  color: rgba(255, 255, 255, 0.781);
  font-size: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}
.booking-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 30px;
  color: rgba(255, 255, 255, 0.966);
  font-size: 14px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  margin: 6px auto;
}
.start-btn {
  display: block;
  width: 100%;
  margin-top: 16px;
  padding: 16px 24px;
  font-size: 20px !important;
  font-weight: 600 !important;
  color: #fff;
  background: linear-gradient(to right, #22c55e 50%, #86efac 50%);
  background-size: 200% 100%;
  background-position: right;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(34, 197, 94, 0.4);
}
.start-btn.filling {
  animation: fill-btn var(--fill-duration) linear forwards;
}
@keyframes fill-btn {
  from {
    background-position: right;
  }
  to {
    background-position: left;
  }
}
.start-btn:hover {
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.58);
}

/* Administrative access dialog */
.admin-card {
  background: rgba(15, 22, 40, 0.92) !important;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 18px !important;
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.6) !important;
  color: #fff !important;
}
.admin-card-header {
  padding: 30px 32px 18px;
  text-align: center;
  background: linear-gradient(180deg, rgba(96, 165, 250, 0.07), transparent);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.admin-title {
  font-size: 21px !important;
  font-weight: 600 !important;
  margin: 0 0 6px !important;
  color: #fff;
}
.admin-subtitle {
  font-size: 13px !important;
  color: #fff;
  margin-bottom: 0 !important;
  margin-top: 10px !important;
  text-align: center;
}
.admin-card-body {
  padding: 24px 28px 4px !important;
}
.admin-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 10px !important;
  box-shadow: none !important;
}
.admin-select >>> input,
.admin-select >>> .v-select__selection,
.admin-select >>> .v-label {
  color: #fff !important;
}
.admin-card-actions {
  padding: 12px 24px 22px !important;
}
.admin-card-actions .v-btn {
  text-transform: none !important;
  border-radius: 10px !important;
}
.admin-confirm-btn {
  background: linear-gradient(135deg, #3b82f6, #2563eb) !important;
  color: #fff !important;
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.4) !important;
}

@media (max-width: 600px) {
  .kiosk-clock {
    top: 14px;
    right: 16px;
  }
  .clock-time {
    font-size: 34px !important;
  }
  .clock-date {
    font-size: 11px !important;
  }
  .kiosk-brand {
    top: 18px;
    left: 16px;
  }
  .brand-text {
    font-size: 12px !important;
  }
  .card-header {
    padding: 22px 18px 16px;
  }
  .card-body {
    padding: 18px;
  }
}
</style>
